import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";
import { fetchSurveyServices } from "../utils/api";
import DashboardSidebar from "../components/DashboardSidebar";

function downloadText(filename, text, mimeType) {
  const blob = new Blob([text], { type: mimeType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export default function ServicesPage() {
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [searchText, setSearchText] = useState("");

  /* ── Load all submissions once on mount ── */
  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchSurveyServices("", "");
      setAllRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load survey submissions.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  /* ── Unique service names for the dropdown ── */
  const serviceOptions = useMemo(() => {
    const names = [...new Set(allRows.map((r) => r.serviceName).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  /* ── Client-side filtering ── */
  const filteredRows = useMemo(() => {
    const q = searchText.toLowerCase();
    return allRows.filter((r) => {
      const matchService = !serviceFilter || r.serviceName === serviceFilter;
      const matchSearch =
        !q ||
        (r.submittedBy || "").toLowerCase().includes(q) ||
        (r.serviceName || "").toLowerCase().includes(q) ||
        String(r.id).includes(q);
      return matchService && matchSearch;
    });
  }, [allRows, serviceFilter, searchText]);

  const downloadCsv = () => {
    if (!filteredRows.length) return;
    const headers = ["Id", "Service Name", "Submitted By", "Submitted On"];
    const values = filteredRows.map((r) => [r.id, r.serviceName, r.submittedBy, r.submittedOn]);
    const csv = [
      headers.join(","),
      ...values.map((row) =>
        row.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")
      ),
    ].join("\n");
    downloadText("survey-submissions.csv", csv, "text/csv");
  };

  return (
    <div className="page-dashboard" style={{ minHeight: "100vh" }}>
      <DashboardSidebar onLogout={handleLogout} />

      <div className="dash-body">

        {/* ── Top bar ── */}
        <nav className="dash-topbar">
          <div className="topbar-left">
            <h2 className="topbar-title">Survey Submissions</h2>
            <span className="topbar-greeting">
              {loading ? "Loading…" : `${filteredRows.length} of ${allRows.length} submission${allRows.length !== 1 ? "s" : ""}`}
            </span>
          </div>
          <div className="topbar-right">
            <button className="btn-outline-action btn-sm" onClick={loadRows} disabled={loading} title="Refresh">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Refresh
            </button>
            <button className="btn-outline-action btn-sm" onClick={downloadCsv} disabled={!filteredRows.length}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          </div>
        </nav>

        {error && <div className="form-alert alert-error" style={{ margin: "16px 24px 0" }}>{error}</div>}

        <div className="dash-content fade-in">
          <div className="dash-card">

            {/* ── Filter bar ── */}
            <div className="dash-table-header">
              <h5>Submitted Surveys ({filteredRows.length})</h5>
              <div className="dash-table-controls">
                <select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  className="dash-page-size"
                  style={{ minWidth: 200 }}
                >
                  <option value="">All services</option>
                  {serviceOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by ID, service, user…"
                  className="dash-search"
                />
              </div>
            </div>

            {/* ── Table ── */}
            <div className="table-responsive">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Service / Condition</th>
                    <th>Submitted By</th>
                    <th>Submitted On</th>
                    <th>View</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted" style={{ padding: "32px 0" }}>
                        Loading submissions…
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted" style={{ padding: "32px 0" }}>
                        {allRows.length === 0 ? "No submissions found." : "No results match your filter."}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r) => (
                      <tr key={r.id}>
                        <td className="td-id">{r.id}</td>
                        <td>{r.serviceName || "General Survey"}</td>
                        <td>{r.submittedBy || "N/A"}</td>
                        <td>{r.submittedOn || "N/A"}</td>
                        <td>
                          <Link className="btn-outline-action btn-sm" to={`/survey/results/${r.id}`}>
                            View Details
                          </Link>
                        </td>
                        <td>
                          <button
                            className="btn-outline-action btn-sm"
                            onClick={() => downloadText(`survey-${r.id}.json`, r.surveyJson || "{}", "application/json")}
                          >
                            JSON
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
